import { graphql, useStaticQuery } from 'gatsby';
import React, { useState, useEffect } from 'react';
import LinksContainer from './links-container';
import ButtonsContainer from './buttons-container';
import MobileNav from './mobile-nav/mobile-nav';
import Hamburger from '../../../images/hamburger.svg';
import Close from '../../../images/close.svg';
import RulaLogo from '../../../images/rula_black.svg';
import NavBanner from "../navigation/nav-banner";
import AniLink from "gatsby-plugin-transition-link/AniLink";
import * as E from '../../../elementor';

interface NavProps {
  ctaColor?: 'amethist' | 'sky' | 'fern' | 'amber',
  ctaLink?: string,
  ctaText?: string,
  customNavBanner?: E.Section,
  disableNavBanner?: boolean
}

const useBannerState = () => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  useEffect(() => {
    let bannerClosed: string | null = "true";
    if (typeof window !== 'undefined') {
      bannerClosed = localStorage.getItem("newBannerClosed");
    }
    setIsBannerVisible(bannerClosed !== "true");
  }, []);

  const handleBannerClose = () => {
    setIsBannerVisible(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem("newBannerClosed", "true");
    }
  };

  return { isBannerVisible, handleBannerClose }
};

const Navigation = ( { ctaColor = 'amethist', ctaText = 'Get started', ctaLink = 'https://www.rula.com/therapy-sign-up-form/', customNavBanner = undefined, disableNavBanner = false }: NavProps) => {
  const [isOpenMobile, setIsOpenMobile] = useState(false)
  const { isBannerVisible, handleBannerClose } = useBannerState()
  
  const displayNavBanner = customNavBanner !== undefined && isBannerVisible && !disableNavBanner
  const navStyle = displayNavBanner ? { top: '52px' } : { top: '0' }
  const toggleMenu = () => setIsOpenMobile(prevOpen => !prevOpen)


  let observer: IntersectionObserver

  useEffect(() => {
    observer = new IntersectionObserver(entries => {
      let footerVisible = false, formHeroVisible = false

      // Check if the observed entry is the form or the footer
      entries.forEach(entry => {
        if (entry.target.classList.contains('hero-form-wrapper')) {
          formHeroVisible = entry.isIntersecting;
        } else if (entry.target.classList.contains('rula-footer-wrapper')) {
          footerVisible = entry.isIntersecting;
        } else if (entry.target.classList.contains('two-funnel-form')) {
          formHeroVisible = entry.isIntersecting;
        }
      })

    }, { threshold: 0.1 });

    const footer = document.querySelector('.rula-footer-wrapper');
    const form = document.querySelector('.hero-form-wrapper');
    const form2f2f = document.querySelector('.two-funnel-form')
    if (form) observer.observe(form);
    if (footer) observer.observe(footer);
    if (form2f2f) observer.observe(form2f2f)

    // Cleanup observer on component unmount
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if(isOpenMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [isOpenMobile])

  const result  = useStaticQuery(graphql`
    query HeaderQueryRula {
      allWpMenuItem(filter: {locations: {eq: GATSBY_HEADER_MENU}, parentId: {eq: null}}) {
        headerMenuItems: nodes {
          label
          uri
          childItems {
            nodes {
              label
              uri
              menuIcons {
                icon {
                  altText
                  link
                  mediaItemUrl
                }
              }
              childItems {
                nodes {
                  uri
                  label
                }
              }
            }
          }
        }
        totalCount
      }
    }
  `)

  const menuItems = result.allWpMenuItem.headerMenuItems;

  // Ensure that the hamburger menu is closed when the user
  // navigates to the entry form.
  const onClickHideMenu = () => {
    toggleMenu() 
  }

  return (
    <>
      { displayNavBanner && <NavBanner onBannerClose={handleBannerClose} customNavBanner={customNavBanner} /> }
      <div className='rula-nav-container' style={navStyle}>
        <AniLink aria-label="Home" fade to="/" >
          <img className='rula-logo' src={RulaLogo} alt="Rula Logo" />
        </AniLink>
        <button className='mobile-nav-controls' onClick={toggleMenu}>
          <img className={`hamburger ${isOpenMobile ? 'hidden' : ''}`} src={Hamburger} alt='hamburger menu' />
          <img className={`close ${isOpenMobile ? '' : 'hidden'}`} src={Close} alt='close menu' />
        </button>
        <LinksContainer items={menuItems} />
        <ButtonsContainer ctaColor={ctaColor} ctaLink={ctaLink} ctaText={ctaText} />
        <MobileNav isOpen={isOpenMobile} items={menuItems} ctaColor={ctaColor} ctaText={ctaText} ctaLink={ctaLink} onClickHideMenu={onClickHideMenu} />
      </div>
    </>
  )
};

export default Navigation;
